import { notify } from 'app/services/errorReporter';
import { AUTH_KEY } from 'app/models/localStorage/constants';
import { getItem } from 'app/models/localStorage/selectors';
import { removeItem, setItem } from 'app/models/localStorage/actions';
import {
  getCurrentUser,
  postConfirm,
  postForgotPassword,
  postOauthRevoke,
  postOauthToken,
  postRefreshOauthToken,
  postResetPassword,
  postSendConfirmation,
  postSendUnlock,
  postUnlock,
} from 'app/utils/api';
import { updateFromServer } from 'app/remote';
import { setTimezoneAndLocaleFromUserInfo } from 'app/models/i18n/actions';
import { createAction } from 'redux-actions';
import { request } from 'app/models/request/actions';
import {
  addLocalStorageListener,
  isStorageClearEvent,
  nowSeconds,
} from 'app/utils/env';
import { externalOauthLogin, finishOauthLogin } from 'app/services/oauth/login';
import * as Oauth from 'app/services/oauth/attach';
import { applyMixins } from 'app/utils/mixins';
import { withOauthErrors } from 'app/models/request/oauth';
import ApplicationError from 'app/models/request/ApplicationError';
import {
  CLEAR_AUTH,
  CLEAR_USER,
  DONE,
  INVALID_TOKEN_ERROR,
  LOAD,
  NOT_LOGGED_IN_ERROR,
  SET_AUTH,
  SET_USER,
} from 'app/models/auth/constants';
import { LocalizationService } from 'app/services/localization';
import { createMockableFunction } from 'app/utils/mock';
import { NonCriticalError } from 'app/constants/NonCriticalError';
import { CookieMismatchError, NoopInteractions } from 'app/services/cookies';
import createErrorType from 'app/utils/createErrorType';
import FluentdLogger from 'app/services/fluentdLogger';
import Logger from 'app/services/logger';
import { fetchEntities } from 'app/models/entities/remote';
import { getResponseId } from 'app/remote/response';
import { User } from 'app/constants/schemas';

export const done = createAction(DONE);
export const load = createAction(LOAD);
export const setUser = createAction(SET_USER);
export const clearUser = createAction(CLEAR_USER);
export const setAuth = createAction(SET_AUTH);
export const clearAuth = createAction(CLEAR_AUTH);

export const AUTH_IN_PROGRESS = 'AUTH_IN_PROGRESS';
export const AUTH_WAIT_TIMEOUT = 5000;

export const setUserAndLocale = (data) => (dispatch) => {
  dispatch(updateFromServer(data));
  dispatch(setUser(data));
  if (globalLocalizationService) {
    dispatch(setTimezoneAndLocaleFromUserInfo(globalLocalizationService));
  }
};

// TODO: Make Auth into a state class+React provider combo and remove this global state.
let lastAuth;
let globalLocalizationService;
let globalCookieInterface;

export const init = ({
  localizationService = new LocalizationService(), // Default value for tests
  cookieInterface = NoopInteractions, // Default value for tests
} = {}) => (dispatch, getState) => {
  globalLocalizationService = localizationService;
  globalCookieInterface = cookieInterface;

  try {
    // This is the only point where we can get back
    // AUTH_IN_PROGRESS. It can only happen if another tab started
    // auth before this one was loaded.
    lastAuth = Promise.resolve(getItem(getState(), AUTH_KEY));
  } catch (error) {
    dispatch(removeItem(AUTH_KEY));
    globalCookieInterface.clearSharedCookie();
    lastAuth = Promise.resolve(null);
    notify(error, { name: 'Auth.init', message: 'getItem failed' });
  }

  return dispatch(checkAuth())
    .then(tokens => {
      if (tokens) {
        dispatch(setAuth(tokens));
        return dispatch(loadUser(tokens, true))
          .then(() => tokens)
          .catch(async (reason) => {
            if (reason instanceof CookieMismatchError) {
              // we are not in a AUTH_IN_PROGRESS context so we do
              // a full revoke process
              await dispatch(revokeAuth());
              return null;
            }
            return await dispatch(refreshOnBadAuth(reason));
          });
      } else {
        // we're logged out
        return null;
      }
    })
    .catch(error => {
      if (error instanceof SiblingTabTimeoutError) {
        // Don't send SiblingTabTimeoutError to Honeybadger because it is not
        // actionable (it probably just represents a temporary network issue).
        Logger.error('Sibling tab timeout:', error);
        FluentdLogger.info({
          name: error.name,
          message: error.message,
        });
      } else {
        notify(error, { name: 'Auth.init' });
      }
      return null;
    });
};

/**
 * @returns {(dispatch: import('redux').AppDispatch) => Promise<unknown>}
 */
export const watchStorage = (storageEvent, timeoutMs = AUTH_WAIT_TIMEOUT) => dispatch => {
  // Make clear events behave the same as setting the AUTH_KEY to null.
  const key = isStorageClearEvent(storageEvent) ? AUTH_KEY : storageEvent.key;
  const newValue = isStorageClearEvent(storageEvent) ? null : storageEvent.newValue;

  if (key === AUTH_KEY) {
    try {
      const newAuth = newValue && JSON.parse(newValue);

      if (newAuth === AUTH_IN_PROGRESS) {
        return dispatch(respectSiblingTabs(timeoutMs));
      } else {
        return lastAuth;
      }
    } catch (error) {
      notify(error, { name: 'Auth.watchStorage', context: { key, newValue } });
      dispatch(removeItem(AUTH_KEY));
      return Promise.reject(error);
    }
  } else {
    return lastAuth;
  }
};

const oauthRequest = applyMixins(
  withOauthErrors,
)(request);

export const authenticate = (username, password) => (dispatch) => {
  const requestOptions = postOauthToken(username, password);
  const onAuth = () => dispatch(oauthRequest(requestOptions));
  return dispatch(doAuth(onAuth));
};

export const reauthenticateWithScopes = (username, password, scopes) => (dispatch) => {
  const requestOptions = postOauthToken(
    username,
    password,
    scopes,
  );
  const onAuth = () => dispatch(oauthRequest(requestOptions));
  return dispatch(doAuth(onAuth, true));
};

const doStartAuthenticateWithOauth = (
  isReauthentication,
  { provider, extraParams, returnUriReference, intent, brandId },
  deps,
) =>
  externalOauthLogin(
    { provider, extraParams, returnUriReference, isReauthentication, intent, brandId },
    deps,
  );

export const startAuthenticateWithOauth = (args, deps) =>
  doStartAuthenticateWithOauth(false, args, deps);

export const startReauthenticateWithOauth = (args, deps) =>
  doStartAuthenticateWithOauth(true, args, deps);

export const finishAuthenticateWithOauth = () =>
  async (dispatch) => {
    const result = await finishOauthLogin();
    const { isReauthentication } = result;
    if (result.success) {
      await dispatch(doAuth(async () => result.token, isReauthentication));
    }
    return result;
  };

export const startAttachExternalOauth = (
  provider,
  returnUriReference,
  brandId,
  dmmEnv,
) => async (dispatch) => {
  const tokens = await dispatch(checkAuth());
  if (!tokens.access_token) {
    throw new Error('Attempt to attach external auth without valid auth');
  }
  return Oauth.startAttachExternalOauth(provider, returnUriReference, brandId, tokens.access_token, dmmEnv);
};

export const finishAttachExternalOauth = Oauth.finishAttachExternalOauth;

export const revokeAuth = () => (dispatch) => {
  const onAuth = (tokens) => dispatch(revokeTokens(tokens));
  return dispatch(doAuth(onAuth));
};

export const revokeTokens = (tokens) => (dispatch) => {
  if (tokens && tokens.hasPrevAuth) {
    // If we also have a previous authentication token, we have to revoke both
    // tokens as the access_token belongs to the privileged token while the
    // refresh_token belongs to our original token.
    return Promise.all([
      dispatch(revokeToken(tokens.refresh_token)),
      dispatch(revokeToken(tokens.access_token)),
    ]).then(() => null);
  } else {
    // Otherwise, both access_token and refresh_token refer to the same
    // instance, so we only need to revoke one of them
    const tokenToRevoke = tokens && (tokens.refresh_token || tokens.access_token);
    // We're ignoring the result, we only care that the call was
    // successful
    return dispatch(revokeToken(tokenToRevoke)).then(() => null);
  }
};

export const revokeToken = (tokenToRevoke) => (dispatch) => {
  const requestOptions = postOauthRevoke(tokenToRevoke);
  return dispatch(request(requestOptions));
};

export const forgotPassword = (email) => (dispatch) =>
  dispatch(request(postForgotPassword(email)));

export const resetPassword = (token, password) => (dispatch) => {
  const requestOptions = postResetPassword({ token, password });
  return dispatch(request(requestOptions));
};

export const sendUnlock = (email) => (dispatch) =>
  dispatch(request(postSendUnlock(email)));

export const unlock = (token) => (dispatch) =>
  dispatch(request(postUnlock(token)));

export const sendConfirmation = (email) => (dispatch) =>
  dispatch(request(postSendConfirmation(email)));

export const confirm = (token) => (dispatch) =>
  dispatch(request(postConfirm(token)));

// Runs the request with session credentials and eventually tries to
// refresh the token if that fails.
export const sessionRequest = createMockableFunction(options => dispatch =>
  dispatch(checkAuth())
    .then(tokens => {
      if (tokens) {
        // User is logged in
        return dispatch(request({ ...options, token: tokens }))
                 .catch(reason =>
                   dispatch(refreshOnBadAuth(reason))
                     .then(newTokens => dispatch(request({ ...options, token: newTokens })))
                     // return original reason
                     .catch(error => Promise.reject(reason)),
                 );
      } else {
        // User is logged out, no auth is used
        return dispatch(request(options));
      }
    }));

// Only to be used externally when we want to refresh user info, like
// after editing own profile
export const refreshAuthUser = () => dispatch =>
  dispatch(fetchEntities({
    params: getCurrentUser(),
    type: User,
    updateAction: () => (_dispatch) => { /* will be handled by setUserAndLocale */ },
  })).then(data => dispatch(setUserAndLocale(data)));

// This one is low-level and only for internal use. It doesn't do its
// own auth checks because it's meant to be called in context where
// auth is considered valid, like just after logging in or just after
// refreshing tokens
const loadUser = (tokens, hadExistingTokens) => async (dispatch) => {
  const data = await dispatch(fetchEntities({
    params: { ...getCurrentUser(), token: tokens },
    type: User,
    fetchAction: request,
    updateAction: () => (_dispatch) => { /* will be handled by setUserAndLocale */ },
  }));
  const userId = getResponseId(data);
  // we only do an auth check if we had an existing token, new logins
  // will always overwrite the auth cookie
  if (hadExistingTokens) {
    await dispatch(globalCookieInterface.checkAuthCookie(userId));
  }
  await dispatch(globalCookieInterface.setAuthCookie(userId, tokens));
  return await dispatch(setUserAndLocale(data));
};

const validateAuth = tokens => {
  if (tokens === AUTH_IN_PROGRESS) {
    // New authorisations should never resolve to AUTH_IN_PROGRESS
    throw new Error('Auth resolved to "in progress" state. This should never happen.');
  }

  if (tokens !== undefined && tokens !== null && typeof tokens !== 'object') {
    throw new Error('Auth should resolve to undefined, null or an object');
  }
};

// `replaceAuth` is replacing current `lastAuth` promise with a new
// value. It doesn't differentiate where it comes from. `action` is
// supposed to return a promise with new auth value (falsy for
// logged-out state). `keepPrevAuth` keeps the existing `lastAuth`
// if it fails and inserts the original refresh token and scope, this
// is to return to the original unprivileged token after our privileged
// token expires
const replaceAuth = (action, keepPrevAuth) => dispatch => {
  const start = oldTokens => {
    dispatch(load());

    return action(oldTokens).then(newTokens => {
      validateAuth(newTokens);

      if (isSameAuth(oldTokens, newTokens)) {
        dispatch(done());
        return newTokens;
      }

      if (newTokens) {
        let newTokensWithRefresh = newTokens;
        if (keepPrevAuth) {
          newTokensWithRefresh = {
            ...newTokens,
            refresh_token: oldTokens.refresh_token,
            hasPrevAuth: true,
          };

          // revoke previous token if we already had an old token
          if (oldTokens.hasPrevAuth) {
            dispatch(revokeToken(oldTokens.access_token));
          }
        }
        dispatch(setAuth(newTokensWithRefresh));
        return dispatch(loadUser(
          newTokensWithRefresh,
          !(oldTokens === undefined || oldTokens === null),
        )).then(() => {
          dispatch(done());
          return newTokensWithRefresh;
        }).catch(async (error) => {
          if (error instanceof CookieMismatchError) {
            // we're within an AUTH_IN_PROGRESS so we just
            // revoke the tokens directly
            await dispatch(revokeTokens(newTokens));
          }
          return Promise.reject(error);
        });
      } else {
        dispatch(clearUser());
        dispatch(clearAuth());
        dispatch(done());
        return newTokens;
      }
    })
    .catch(async (error) => {
      if (!keepPrevAuth) {
        dispatch(clearUser());
        dispatch(clearAuth());
      }

      dispatch(done());
      return Promise.reject(error);
    });
  };

  const oldAuth = lastAuth;
  const newAuth = lastAuth.then(start, () => start(null));
  if (keepPrevAuth) {
    lastAuth = newAuth.then(v => v, () => oldAuth);
  } else {
    lastAuth = newAuth;
  }

  return newAuth;
};

const isSameAuth = (tokensA, tokensB) => {
  const { access_token: a } = tokensA || {};
  const { access_token: b } = tokensB || {};
  return a === b;
};

// `doAuth` is only meant to be used when current client (or tab) is
// taking care of the auth. To lock other tabs and other requests in
// current tabs from doing auth, we're setting AUTH_IN_PROGRESS flag.
//
// `getNewAuth` parameter needs to be a function that receives old
// tokens (or `null` if logged out) and returns a promise resolving
// to either:
//   - new auth tokens
//   - null if logging out
//   - rejected promise if there was an error
const doAuth = (getNewAuth, keepPrevAuth) => (dispatch, getState) => {
  if (getItem(getState(), AUTH_KEY) === AUTH_IN_PROGRESS) {
    // Don't do new auth when some other auth is in progress. This can
    // happen when mulitple requests failed with 401 simultaneously
    // for example
    return lastAuth;
  }

  // Set the flag so that no other ongoing process is going to start
  // auth simultaneously
  dispatch(setItem(AUTH_KEY, AUTH_IN_PROGRESS));
  globalCookieInterface.clearSharedCookie();

  const nextAuth = dispatch(replaceAuth(oldTokens => (
    getNewAuth(oldTokens)
      .then(newTokens => {
        if (newTokens) {
          // Add received_at so that we can use local time instead of
          // server time to see if we need to refresh token. This is
          // due to possible different system clock settings.
          return {
            ...newTokens,
            received_at: nowSeconds(),
          };
        } else {
          return newTokens;
        }
      })
  ), keepPrevAuth));

  // Only after the replacing local auth info (and reloading the user)
  // is successful, save auth info in localStorage. This will also
  // notify other tabs listening on the `storage` event.
  return nextAuth
    .then(newTokens => {
      if (newTokens) {
        dispatch(setItem(AUTH_KEY, newTokens));
      } else {
        // Empty tokens means we've logged out
        dispatch(removeItem(AUTH_KEY));
        dispatch(globalCookieInterface.cleanCookieData());
      }

      return newTokens;
    })
    .catch(error => {
      if (keepPrevAuth) {
        lastAuth.then(tokens => dispatch(setItem(AUTH_KEY, tokens)));
      } else {
        dispatch(removeItem(AUTH_KEY));
        dispatch(globalCookieInterface.cleanCookieData());
      }
      return Promise.reject(error);
    });
};

export class SiblingTabTimeoutError extends createErrorType('SiblingTabTimeoutError') {}

// Waits for another tab to finish auth and tries to use those
// credentials
const respectSiblingTabs = (timeoutMs = AUTH_WAIT_TIMEOUT) => dispatch => {
  const siblingAuth = new Promise((resolve, reject) => {
    const removeLocalStorageListener = addLocalStorageListener(watch);

    // Give it limited time of seconds until we decide it failed
    const timeout = setTimeout(giveUp, timeoutMs);

    function watch(storageEvent) {
      // Make clear events behave the same as setting the AUTH_KEY to null.
      const key = isStorageClearEvent(storageEvent) ? AUTH_KEY : storageEvent.key;
      const newValue = isStorageClearEvent(storageEvent) ? null : storageEvent.newValue;

      if (key === AUTH_KEY) {
        clearTimeout(timeout);
        removeLocalStorageListener();

        try {
          if (newValue) {
            resolve(JSON.parse(newValue));
          } else {
            resolve(null);
          }
        } catch (error) {
          reject(new Error('Auth tokens have been tempered with'));
        }
      }
    }

    function giveUp() {
      removeLocalStorageListener();

      // To prevent waiting again, we clear the AUTH_KEY from
      // localStorage. If there's a concurrent auth process in another
      // tab, it will set the AUTH_KEY again but this tab will be out
      // not catch it.
      dispatch(removeItem(AUTH_KEY));

      reject(new SiblingTabTimeoutError(
        `Waited for the other tab ${AUTH_WAIT_TIMEOUT / 1000}s to authenticate. Giving up.`,
      ));
    }
  });

  // It's important that the code above is called straight away
  // because of possible race condition when we're too late to catch
  // the `storage` event.
  return dispatch(replaceAuth(() => siblingAuth));
};

const checkAuth = () => dispatch => {
  const action = tokens => {
    if (tokens === AUTH_IN_PROGRESS) {
      // lastAuth should resolve to AUTH_IN_PROGRESS only if there's
      // another tab that started AUTH when this tab was initially
      // loading
      return dispatch(respectSiblingTabs());
    } else if (needsRefresh(tokens)) {
      return dispatch(refreshTokens(tokens));
    } else {
      return tokens;
    }
  };

  // The `catch` part is ignore previous failed auths
  return lastAuth.then(action, () => action(null));
};

const refreshOnBadAuth = reason => dispatch => {
  if (reason instanceof ApplicationError &&
    (reason.code === INVALID_TOKEN_ERROR || reason.code === NOT_LOGGED_IN_ERROR)
  ) {
    // The server explicitly rejected the request because of a bad
    // token. We need to either refresh the token, or remove it if it
    // could not be refreshed. This will happen if the token has been
    // revoked, or if there is no refresh token available.
    return dispatch(refreshTokens());
  } else {
    // Just reject with original reason otherwise
    return Promise.reject(reason);
  }
};

const refreshTokens = () => (dispatch) => {
  const onAuth = (tokens) => {
    if (tokens && tokens.refresh_token) {
      return dispatch(oauthRequest(postRefreshOauthToken(tokens)));
    } else {
      return Promise.reject(new NonCriticalError('No refresh token'));
    }
  };

  return dispatch(doAuth(onAuth));
};

const needsRefresh = tokens => {
  // We assume we're in logged out session so refreshing auth tokens doesn't apply
  if (!tokens) {
    return false;
  }

  const { refresh_token, received_at, expires_in } = tokens;

  // No way to fresh without the refresh_token
  if (!refresh_token) {
    return false;
  }

  return (received_at + expires_in - 2) < nowSeconds();
};
